import {
    Button,
    ButtonLink,
    Typography,
    Stack,
    StackSpecificProps,
    Content,
    ButtonProps,
    Heading,
} from '@the-game/components'
import styled from '@emotion/styled'
import { Copy, Check } from '@the-game/components/icons'
import React, { useEffect, useState } from 'react'
import { calcRem, fontScale } from '@the-game/components/utils'
import { environment } from '../../environments/environment'
import { ShareCompetitionLink } from './share-competition'
import {
    useGtmDispatch,
    competitionCodeCopiedEvent,
    competitionEmailEvent,
} from '../Gtm'
import { useGameContext } from '@the-game/web-common'
import { GameName, SportName } from '@the-game/api-interfaces'

const StyledFailedToCopyMessage = styled('span')(({ theme }) => ({
    marginTop: calcRem(theme.spacing.xs),
    color: theme.colors.palette.red500,
    fontSize: calcRem(fontScale(0.75)),
    lineHeight: calcRem(fontScale(1.125)),
    minHeight: calcRem(fontScale(1.125)),
}))

export const StyledCodeBlock = styled('div')(({ theme }) => ({
    backgroundColor: theme.colors.palette.neutral30,
    fontSize: calcRem(theme.spacing.lg),
    color: theme.colors.palette.neutral70,
    fontWeight: theme.weights.bold,
    textTransform: 'uppercase',
    padding: calcRem(theme.spacing.sm),
}))

export const StyledCompetitionHeading = styled('span')(({ theme }) => ({
    color: theme.colors.primary,
    fontSize: calcRem(fontScale(2.375)),
    fontWeight: theme.weights.bold,
    textAlign: 'center',
}))

const iconSize = 77

export const StyledSuccessCheck = styled('div')(({ theme }) => ({
    width: iconSize,
    height: iconSize,
    border: `solid 8px ${theme.colors.palette.green500}`,
    boxSizing: 'content-box',
    borderRadius: '50%',
    fill: theme.colors.palette.green500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    svg: {
        width: iconSize * 0.6,
        height: iconSize * 0.6,
    },
}))

export interface CopyButtonProps extends ButtonProps {
    textToCopy: string
    event?: () => void
}

export function CopyButton({
    textToCopy,
    children,
    event = () => undefined,
    ...props
}: CopyButtonProps) {
    enum CopyState {
        Default,
        Copying,
        Done,
        Failed,
    }

    const [copyState, setCopyState] = useState<CopyState>(CopyState.Default)

    const copy = async () => {
        try {
            setCopyState(CopyState.Copying)
            await navigator.clipboard.writeText(textToCopy)
            setCopyState(CopyState.Done)
            event()
        } catch (err) {
            setCopyState(CopyState.Failed)
            if (err instanceof DOMException && err.name === 'NotAllowedError') {
                // normally write permission is given to active tab over https.
                // user must have explicitly denied permission to write to the clipboard,
                // which we don't want to end up in error logs
                return
            }
            throw err
        }
    }
    return (
        <div>
            <Button onClick={copy} {...props}>
                <Copy size="md" />

                {copyState === CopyState.Done ? 'Copied!' : children}
            </Button>
            {copyState === CopyState.Failed && (
                <StyledFailedToCopyMessage>
                    Failed to copy. You may need to try a different browser
                </StyledFailedToCopyMessage>
            )}
        </div>
    )
}

export function CopyInviteLinkButton({
    id,
    event,
    disabled,
}: {
    id: string
    event: () => void
    disabled?: boolean
}) {
    return (
        <CopyButton
            textToCopy={`${environment.baseUrl}/c/${id}`}
            event={event}
            fluid
            disabled={disabled}
        >
            Copy invite link
        </CopyButton>
    )
}

export function ShareCompetitionButton({
    id,
    variant,
    name,
    sport,
    game,
    event = () => undefined,
    disabled,
    lockoutRound,
}: {
    id: string
    name: string
    sport: SportName
    game: GameName
    event: () => void
    variant?: 'ghost'
    disabled?: boolean
    lockoutRound?: number
}) {
    return (
        <ShareCompetitionLink
            variant={variant}
            competitionName={name}
            competitionUrl={`${environment.baseUrl}/c/${id}`}
            sport={sport}
            game={game}
            event={event}
            lockoutRound={lockoutRound}
            disabled={disabled}
        >
            Share via email
        </ShareCompetitionLink>
    )
}

export function Success({
    compId,
    competitionName,
    lockoutRound,
}: {
    compId: string
    competitionName: string
    lockoutRound?: number
}) {
    const raiseEvent = useGtmDispatch()
    const copyEvent = () => raiseEvent(competitionCodeCopiedEvent(compId))
    const emailEvent = () => raiseEvent(competitionEmailEvent(compId))

    const { sport, game } = useGameContext()

    const stack: StackSpecificProps = {
        justifyContent: 'center',
        alignItems: 'center',
        direction: 'column',
    }

    useEffect(() => {
        // change the URL from /create to the actual ID
        // based on errors in sumologic and some created competitions,
        // we suspect people are copying the URL from this page and giving to friends
        // we're not using react-router here because that would navigate to details instead
        window.history.replaceState(
            null,
            window.document.title,
            window.location.href.replace('/create', `/${compId}`),
        )
    }, [compId])

    return (
        <Content layout="full">
            <Stack gap="xl" {...stack}>
                <Stack gap="lg" {...stack}>
                    <StyledSuccessCheck>
                        <Check />
                    </StyledSuccessCheck>
                    <Heading textAlign="center">Competition created</Heading>
                </Stack>
                <Stack {...stack}>
                    <Heading level="2" size="4">
                        Your competition:
                    </Heading>
                    <StyledCompetitionHeading>
                        {competitionName}
                    </StyledCompetitionHeading>
                </Stack>
                <Stack gap="xs" {...stack}>
                    <Heading level="3" size="5">
                        Your invite code
                    </Heading>
                    <Typography variant="p" textAlign="center">
                        Use the code below to add members to this competition
                    </Typography>
                    <StyledCodeBlock>{compId}</StyledCodeBlock>
                </Stack>
                <Stack direction="column" gap="md">
                    <CopyInviteLinkButton id={compId} event={copyEvent} />
                    <ShareCompetitionButton
                        id={compId}
                        name={competitionName}
                        sport={sport}
                        game={game}
                        event={emailEvent}
                        lockoutRound={lockoutRound}
                    />

                    <ButtonLink
                        variant="ghost"
                        fluid
                        to={`/${sport}/${game}/comp/${compId}`}
                    >
                        View competition
                    </ButtonLink>
                </Stack>
            </Stack>
        </Content>
    )
}

export default Success
